/**
 * Shared API client utilities
 *
 * Provides authentication headers and standardized fetch wrappers
 * (JSON + multipart) for the shipments UI plugin.
 *
 * The wrappers throw {@link ApiError} on failure and return the unwrapped
 * `data` payload directly, so callers don't repeat success/error unwrapping.
 * Paths are RELATIVE (`/api/...`) so they work behind the Vite dev proxy.
 */

import {
  ApiError,
  getApiHeaders,
  handleUnauthorized,
  type StandardApiResponse,
} from '@lognai/plugin-sdk';
import { buildQueryString, buildPaginationQuery } from '@lognai/frontend-core';

export { buildQueryString, buildPaginationQuery };
export type { ListQueryParams } from '@lognai/frontend-core';
export type { PaginationParams } from '@lognai/plugin-sdk';

/**
 * Standardized fetch wrapper with error handling.
 */
export const apiFetch = async <T>(
  url: string,
  options: RequestInit = {}
): Promise<T> => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getApiHeaders(),
      ...options.headers,
    },
  });

  if (response.status === 401) {
    handleUnauthorized();
    throw new ApiError('Unauthorized', { type: 'unauthorized' });
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      message: response.statusText,
    }));
    throw new ApiError(error.error || error.message || `HTTP ${response.status}`, error.details);
  }

  const apiResponse: StandardApiResponse<T> = await response.json();

  if (!apiResponse.success) {
    throw new ApiError(apiResponse.error || 'API request failed', (apiResponse as any).details);
  }

  return apiResponse.data;
};

/**
 * Multipart/form-data fetch wrapper.
 *
 * Uses the same auth headers as {@link apiFetch} but DELETES Content-Type so the
 * browser sets the multipart boundary itself — otherwise the server's JSON body
 * parser swallows the request and the upload fails.
 */
export const apiFetchMultipart = async <T>(
  url: string,
  formData: FormData,
  options: RequestInit = {}
): Promise<T> => {
  const headers = {
    ...getApiHeaders(),
    ...(options.headers as Record<string, string> | undefined),
  } as Record<string, string>;
  delete headers['Content-Type'];

  const response = await fetch(url, {
    method: 'POST',
    ...options,
    headers,
    body: formData,
  });

  if (response.status === 401) {
    handleUnauthorized();
    throw new ApiError('Unauthorized', { type: 'unauthorized' });
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      message: response.statusText,
    }));
    throw new ApiError(error.error || error.message || `HTTP ${response.status}`, error.details);
  }

  const apiResponse: StandardApiResponse<T> = await response.json();

  if (!apiResponse.success) {
    throw new ApiError(apiResponse.error || 'API request failed', (apiResponse as any).details);
  }

  return apiResponse.data;
};
